
import Button from 'primevue/button';
import {
  computed,
  defineComponent, onMounted, onBeforeUnmount, nextTick, Ref, ref, SetupContext, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import EditorModal from '@/components/modals/EditorModal.vue';
// SelectButton removed - using custom filter options
import Loader from '@/components/layout/Loader.vue';
import PreviewModal from '@/components/modals/PreviewModal.vue';
import TemplateHtmlPreviewSlider from '@/components/import/TemplateHtmlPreviewSlider.vue';
import { UserState } from '@/composables/User';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { fetchUserTemplates, GalleryTemplateItem } from '@/composables/template/TemplateHelper';
import { TemplateTypeEnum } from '@/composables/shop/Templates';
import { useTemplatesApi } from '@/composables/templates/useTemplatesApi';
import { asInt, showToastError, showToastSuccess } from '@/helpers';
import { store } from '@/store';
import { getTemplateParentTypeByType, isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import { generateNextStepName, TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { getCountOperatorsByType, mappingTemplateTypeToOperatorType } from '@/composables/AutomatedScenarios';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import { nestCall, NestApiType } from '@/composables/nestApi';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { getFeatureFlagForTemplateType } from '@/configs/feature-flags';
import ComingSoonBadge from '@/components/shared/ComingSoonBadge.vue';
import RequireIntegrationDialog from '@/components/integrations/RequireIntegrationDialog.vue';
import { useIntegrationGating } from '@/composables/integrations/useIntegrationGating';
import { useIntegrationCollections } from '@/composables/integrations/useIntegrationCollections';
import DrilldownBanner from '@/components/integrations/DrilldownBanner.vue';
import { pickLocalizedText } from '@/utils/localized-text';
import IntegrationCollectionCard from '@/components/integrations/IntegrationCollectionCard.vue';

interface TemplateWithShop extends TemplatesCustom {
  id_shop: number;
  createdBy?: string;
  whatsapp_status?: string;
}

interface BaseLinkData {
  namePrimaryTemplate?: string;
  countRedirectTemplate?: number;
  originTemplateId?: number | null;
  selectedTemplate?: any;
}

export default defineComponent({
  name: 'TemplateSelectorV2',

  components: {
    PreviewModal,
    Loader,
    EditorModal,
    Button,
    Dialog,
    ProgressSpinner,
    MultiSelect,
    Dropdown,
    SpmBadge,
    TemplateHtmlPreviewSlider,
    ComingSoonBadge,
    RequireIntegrationDialog,
    IntegrationCollectionCard,
    DrilldownBanner,
  },

  props: {
    typeTemplates: {
      type: String,
      required: true,
      default: '',
    },

    originCall: {
      type: String,
      required: false,
      default: '',
    },

    idCampaign: {
      type: [Number, null] as any,
      required: false,
      default: null,
    },

    duplicate: {
      type: Boolean,
      required: false,
      default: false,
    },

    baseLinkData: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    customTemplateType: {
      type: String,
      required: false,
      default: '',
    },

    forceDuplicate: {
      type: Boolean,
      required: false,
      default: false,
    },

    currentTemplateName: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-choose-template': Object,
  },

  setup(props: {
    typeTemplates: string;
    originCall: string|null;
    idCampaign: number|null;
    duplicate: boolean;
    baseLinkData: BaseLinkData;
    customTemplateType: string;
    forceDuplicate: boolean;
    currentTemplateName: string;
  }, context: SetupContext) {
    const { t, locale } = useI18n();
    const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);
    const baseUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;
    console.log('baseUrl master', baseUrl);

    const imageLoadTime = ref<Record<number, number>>({});
    const imagesLoaded = ref<Record<number, boolean>>({});
    const isListLoading = ref(false);
    const list: Ref<(TemplateWithShop | GalleryTemplateItem)[]> = ref([]);
    const templatesType = ref(props.typeTemplates);
    const displayPreviewModal = ref(false);
    const images = ref([{}]);
    const searchQuery = ref('');
    const limit = 12;
    const displayDialog = ref(false);
    const totalCount = ref(0);
    const isLoadingMore = ref(false);

    // Template types configuration with icons (style like MarketingPressure.vue)
    const listTypesConfig = ref([
      {
        type: TemplateTypeEnum.EMAIL,
        label: t('templateBuilder.fields.email'),
        description: t('templateSelector.dialogTypeTemplate.types.email.description'),
        icon: 'fa-light fa-envelope',
      },
      {
        type: TemplateTypeEnum.SMS,
        label: t('templateSelector.dialogTypeTemplate.types.sms.label'),
        description: t('templateSelector.dialogTypeTemplate.types.sms.description'),
        icon: 'fa-light fa-comment-sms',
      },
      {
        type: TemplateTypeEnum.PUSHNOTIFICATIONS,
        label: t('templateSelector.dialogTypeTemplate.types.pushnotifications.label'),
        description: t('templateSelector.dialogTypeTemplate.types.pushnotifications.description'),
        icon: 'fa-light fa-bell',
      },
      {
        type: TemplateTypeEnum.POPUP,
        label: t('templateSelector.dialogTypeTemplate.types.popup.label'),
        description: t('templateSelector.dialogTypeTemplate.types.popup.description'),
        icon: 'fa-light fa-window-restore',
      },
      {
        type: TemplateTypeEnum.EMBED,
        label: t('templateSelector.dialogTypeTemplate.types.embed.label'),
        description: t('templateSelector.dialogTypeTemplate.types.embed.description'),
        icon: 'fa-light fa-code',
      },
      {
        type: TemplateTypeEnum.RICHTEXTING,
        label: t('templateSelector.dialogTypeTemplate.types.richtexting.label'),
        description: t('templateSelector.dialogTypeTemplate.types.richtexting.description'),
        icon: 'fa-light fa-message-lines',
      },
      {
        type: TemplateTypeEnum.WHATSAPP,
        label: t('templateSelector.dialogTypeTemplate.types.whatsapp.label'),
        description: t('templateSelector.dialogTypeTemplate.types.whatsapp.description'),
        icon: 'fa-brands fa-whatsapp',
      },
    ]);

    const templateCategorySelected = ref({ category: 'recent', title: t('templateSelector.categories.recent'), icon: 'fa-light fa-clock' });
    const templateCategoriesOptions = ref([
      { category: 'recent', title: t('templateSelector.categories.recent'), icon: 'fa-light fa-clock' },
      { category: 'gallery', title: t('templateSelector.categories.gallery'), icon: 'fa-light fa-images' },
      { category: 'self', title: t('templateSelector.categories.self'), icon: 'fa-light fa-user' },
    ]);
    const previousCategory = ref('recent');

    // Author filter (MultiSelect - only for gallery).
    // Shared gating composable + `me` extra option for user-owned templates.
    const selectedAuthors = ref<string[]>([]);
    const {
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      gatePreflight,
      gateBackendResponse,
      authorFilterOptionsFor,
    } = useIntegrationGating();

    // The "me" row is template-specific (other pickers only deal with
    // gallery items). Non-gallery items in `list` are user templates so
    // we don't pass them through the integration filter.
    const authorFilterOptions = authorFilterOptionsFor(
      () => list.value.filter(isGalleryItem),
      () => [{ value: 'me', label: t('templateSelector.filters.authorMe') }],
    );

    // ----- Visual author filter split (same UX as the other pickers) -----
    // ShopiMind row + "me" row sit on top (non-integration). All
    // integration partners are grouped below in their own scrollable
    // subsection with an inline search past 5 entries.

    const authorSearchQuery = ref('');

    /** All author options (ShopiMind + "me" + integrations at the same level),
     *  filtered by the search input. Integration options carry a `count`. */
    const filteredAuthorOptions = computed(() => {
      const q = authorSearchQuery.value.trim().toLowerCase();
      if (!q) return authorFilterOptions.value;
      return authorFilterOptions.value.filter(
        (o) => o.label.toLowerCase().includes(q)
          || o.value.toLowerCase().includes(q),
      );
    });

    const toggleAuthor = (value: string) => {
      const idx = selectedAuthors.value.indexOf(value);
      if (idx >= 0) {
        selectedAuthors.value.splice(idx, 1);
      } else {
        selectedAuthors.value.push(value);
      }
    };

    const getAuthorIcon = (slug: string): string | undefined => {
      if (slug === 'shopimind' || slug === 'me') return undefined;
      const match = list.value.find(
        (item) => isGalleryItem(item) && item.integration_slug === slug,
      );
      if (match && isGalleryItem(match)) {
        return match.integration_icon_url ?? undefined;
      }
      return undefined;
    };

    // Scenario filters (two separate Dropdowns - only for recent)
    const selectedCampaign = ref<{ id: number; name: string; campaignType: string } | null>(null);

    // Automation dropdown state
    const selectedAutomation = ref<{ id: number; name: string; campaignType: string } | null>(null);
    const automationOptions = ref<{ id: number; name: string; campaignType: string }[]>([]);
    const automationLoading = ref(false);

    // Bulk dropdown state
    const selectedBulk = ref<{ id: number; name: string; campaignType: string } | null>(null);
    const bulkOptions = ref<{ id: number; name: string; campaignType: string }[]>([]);
    const bulkLoading = ref(false);

    let campaignFilterDebounce: ReturnType<typeof setTimeout> | null = null;

    const fetchCampaignsByType = async (
      campaignType: 'automation' | 'bulk',
      search = '',
    ) => {
      const optionsRef = campaignType === 'automation' ? automationOptions : bulkOptions;
      const loadingRef = campaignType === 'automation' ? automationLoading : bulkLoading;
      loadingRef.value = true;
      try {
        const queryParams = new URLSearchParams({
          type: templatesType.value,
          limit: '50',
          offset: '0',
          campaignType,
        });
        if (search) {
          queryParams.set('search', search);
        }
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/templates/selector/campaigns',
          {},
          `?${queryParams.toString()}`,
        );
        if (response?.success && Array.isArray(response.data)) {
          optionsRef.value = response.data;
        }
      } catch {
        // silent
      } finally {
        loadingRef.value = false;
      }
    };

    const onAutomationShow = () => {
      if (automationOptions.value.length === 0) {
        fetchCampaignsByType('automation');
      }
    };

    const onBulkShow = () => {
      if (bulkOptions.value.length === 0) {
        fetchCampaignsByType('bulk');
      }
    };

    const onCampaignFilter = (event: any, campaignType: 'automation' | 'bulk') => {
      if (campaignFilterDebounce) clearTimeout(campaignFilterDebounce);
      campaignFilterDebounce = setTimeout(() => {
        fetchCampaignsByType(campaignType, event.value);
      }, 300);
    };

    // When one dropdown is selected, clear the other
    watch(selectedAutomation, (val) => {
      if (val) {
        selectedBulk.value = null;
        selectedCampaign.value = val;
      } else if (!selectedBulk.value) {
        selectedCampaign.value = null;
      }
    });

    watch(selectedBulk, (val) => {
      if (val) {
        selectedAutomation.value = null;
        selectedCampaign.value = val;
      } else if (!selectedAutomation.value) {
        selectedCampaign.value = null;
      }
    });

    // Helper to check if a record is a gallery item
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isGalleryItem = (record: any): record is GalleryTemplateItem => record && record.isGalleryItem === true;

    // Current locale for i18n titles
    const currentLocale = computed(() => locale.value || 'fr');

    // Get localized text from a multilingual object (central util — G12)
    const getLocalizedText = (textObj: Record<string, string> | undefined, fallback = ''): string => {
      if (!textObj || typeof textObj !== 'object') return fallback;
      return pickLocalizedText(textObj, currentLocale.value) || fallback;
    };

    // Get display label for any record type
    const getRecordLabel = (record: TemplateWithShop | GalleryTemplateItem): string => {
      if (isGalleryItem(record)) {
        return getLocalizedText(record.title, 'Sans titre');
      }
      return record.label || '';
    };

    // Get author name from template
    const getAuthorName = (record: TemplateWithShop | GalleryTemplateItem): string => {
      if (isGalleryItem(record)) {
        if (record.author === 'integration') {
          return record.integration_name || record.integration_slug || 'Integration';
        }
        return 'Shopimind';
      }
      if (record.id_shop === 0) {
        return 'ShopiMind';
      }
      return record.createdBy || t('templateSelector.filters.authorMe');
    };

    // Filtered list based on author filter
    const filteredList = ref<(TemplateWithShop | GalleryTemplateItem)[]>([]);

    const updateFilteredList = () => {
      let items = list.value;

      // Apply search filter for gallery items
      if (templateCategorySelected.value.category === 'gallery' && searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        items = items.filter((record) => {
          if (isGalleryItem(record)) {
            const label = getLocalizedText(record.title, '').toLowerCase();
            const desc = getLocalizedText(record.description, '').toLowerCase();
            return label.includes(query) || desc.includes(query);
          }
          return true;
        });
      }

      // If not gallery or no authors selected, show filtered items
      if (templateCategorySelected.value.category !== 'gallery' || selectedAuthors.value.length === 0) {
        filteredList.value = items;
        return;
      }

      // Filter by selected authors. Selection values:
      //   - 'shopimind' → native ShopiMind items
      //   - 'me'        → templates created by the current shop user
      //   - <slug>      → items published by the integration with this slug
      filteredList.value = items.filter((record) => {
        if (isGalleryItem(record)) {
          // Integration-owned item — match by slug.
          if (record.author === 'integration' && record.integration_slug) {
            return selectedAuthors.value.includes(record.integration_slug);
          }
          // Legacy/native gallery items. Old format stores author as plain
          // string ('Shopimind' / something else) — treat anything not
          // marked as integration as ShopiMind.
          if (selectedAuthors.value.includes('shopimind')) {
            return true;
          }
          return false;
        }
        const isShopimind = record.id_shop === 0;
        if (selectedAuthors.value.includes('shopimind') && isShopimind) {
          return true;
        }
        if (selectedAuthors.value.includes('me') && !isShopimind) {
          return true;
        }
        return false;
      });
    };

    watch([list, selectedAuthors, templateCategorySelected], () => {
      updateFilteredList();
    }, { immediate: true, deep: true });

    const getWhatsappStatusLabel = (status: string): string => {
      const key = `templates.whatsappStatus.${status}`;
      const translated = t(key);
      return translated !== key ? translated : status;
    };

    const getWhatsappStatusIcon = (status: string): string => {
      const iconMap: Record<string, string> = {
        APPROVED: 'fa-solid fa-circle-check',
        REINSTATED: 'fa-solid fa-circle-check',
        PENDING: 'fa-solid fa-clock',
        IN_APPEAL: 'fa-solid fa-clock',
        REJECTED: 'fa-solid fa-circle-xmark',
        PAUSED: 'fa-solid fa-circle-pause',
        DISABLED: 'fa-solid fa-circle-minus',
        FLAGGED: 'fa-solid fa-flag',
        ARCHIVED: 'fa-solid fa-box-archive',
        DELETED: 'fa-solid fa-trash',
        PENDING_DELETION: 'fa-solid fa-trash',
        LIMIT_EXCEEDED: 'fa-solid fa-circle-exclamation',
        LOCKED: 'fa-solid fa-lock',
        ERROR: 'fa-solid fa-circle-exclamation',
      };
      return iconMap[status] || 'fa-solid fa-circle-question';
    };

    const getWhatsappStatusColors = (status: string): { bg: string; color: string } => {
      const sendable = ['APPROVED', 'REINSTATED'];
      const pending = ['PENDING', 'IN_APPEAL'];
      if (sendable.includes(status)) return { bg: '#ecfdf5', color: '#059669' };
      if (pending.includes(status)) return { bg: '#fffbeb', color: '#d97706' };
      return { bg: '#fef2f2', color: '#dc2626' };
    };

    const closeModal = () => {
      context.emit('on-close-dialog');
    };

    const getListTemplates = async (loadMore: boolean) => {
      let {
        // eslint-disable-next-line prefer-const
        originCall,
      } = props;

      if (templatesType.value !== 'none') {
        if (loadMore) {
          isLoadingMore.value = true;
        } else {
          isListLoading.value = true;
        }

        const offset = loadMore ? list.value.length : 0;
        const result = await fetchUserTemplates(
          searchQuery.value,
          templateCategorySelected.value.category,
          props.customTemplateType || templatesType.value,
          originCall,
          limit,
          offset,
          selectedCampaign.value?.id,
        );

        if (result) {
          if (loadMore) {
            list.value = [...list.value, ...result.data as (TemplateWithShop | GalleryTemplateItem)[]];
          } else {
            list.value = result.data as (TemplateWithShop | GalleryTemplateItem)[];
          }
          totalCount.value = result.total;
          isListLoading.value = false;
          isLoadingMore.value = false;
          updateFilteredList();

          // Auto-switch to gallery if recent category is empty
          if (!loadMore && templateCategorySelected.value.category === 'recent' && list.value.length === 0) {
            const galleryOption = templateCategoriesOptions.value.find((opt) => opt.category === 'gallery');
            if (galleryOption) {
              templateCategorySelected.value = galleryOption;
            }
          }
        }
      }
    };

    // IntersectionObserver for infinite scroll
    const scrollSentinelRef = ref<HTMLElement | null>(null);
    let scrollObserver: IntersectionObserver | null = null;

    const setupScrollObserver = () => {
      if (scrollObserver) {
        scrollObserver.disconnect();
      }

      scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isLoadingMore.value && !isListLoading.value && list.value.length < totalCount.value) {
            getListTemplates(true);
          }
        },
        { threshold: 0.1 },
      );

      if (scrollSentinelRef.value) {
        scrollObserver.observe(scrollSentinelRef.value);
      }
    };

    watch(scrollSentinelRef, (el) => {
      if (el) {
        setupScrollObserver();
      }
    });

    const imageLoaded = (id_template: number) => {
      // Check if the image is already marked as loaded
      if (!imagesLoaded.value[id_template]) {
        imagesLoaded.value[id_template] = true;
        imageLoadTime.value[id_template] = new Date().getTime();
      }
    };

    const imageRetryCount = ref<Record<number, number>>({});

    const imageFailed = (id_template: number) => {
      // Initialize the counter if not already done
      if (!imageRetryCount.value[id_template]) {
        imageRetryCount.value[id_template] = 0;
      }

      // Check if we've already tried 3 times
      if (imageRetryCount.value[id_template] >= 3) {
        return;
      }

      // Increment the retry counter
      imageRetryCount.value[id_template] += 1;

      // If the image is not marked as loaded
      if (!imagesLoaded.value[id_template]) {
        setTimeout(() => {
          imageLoadTime.value[id_template] = new Date().getTime();
        }, 3000);
      }
    };

    // Helper function to get the first image key from a JSON array or return as is
    const getFirstImageKey = (imageKey: string | undefined | null): string => {
      if (!imageKey) return '';
      try {
        const parsed = JSON.parse(imageKey);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
      } catch {
        // Not JSON, return as is
      }
      return imageKey;
    };

    watch([templateCategorySelected, searchQuery], async (newValues, oldValues) => {
      const [newCategory] = newValues;
      const currentCategory = newCategory.category;

      // Only reload if the category has really changed
      if (currentCategory !== previousCategory.value) {
        previousCategory.value = currentCategory;
        // Reset scenario filters when switching categories
        selectedCampaign.value = null;
        selectedAutomation.value = null;
        selectedBulk.value = null;
        automationOptions.value = [];
        bulkOptions.value = [];
        await getListTemplates(false);
      } else if (oldValues && newValues[1] !== oldValues[1]) {
        // For gallery, filter locally instead of re-fetching
        if (currentCategory === 'gallery') {
          updateFilteredList();
        } else {
          await getListTemplates(false);
        }
      }
    });

    // Re-fetch templates when scenario filter changes
    watch(selectedCampaign, async () => {
      if (templateCategorySelected.value.category === 'recent') {
        await getListTemplates(false);
      }
    });

    const handleChoose = async (idShopOfTemplate: number, idTemplate: number, label: string) => {
      store.commit('general/showTheSpinner');

      // If it's a gallery template, import via Nest API
      if (templateCategorySelected.value.category === 'gallery') {
        try {
          const idShopValue = UserState.activeShop?.id ?? 0;

          if (!idShopValue) {
            showToastError(t('templateSelector.errors.noShop'));
            store.commit('general/hideTheSpinner');
            return;
          }

          const selectedTemplate = list.value.find(
            (tmpl) => !isGalleryItem(tmpl) && tmpl.id_template === idTemplate,
          ) as TemplateWithShop | undefined;

          if (!selectedTemplate?.path) {
            showToastError(t('templateSelector.errors.noPath'));
            store.commit('general/hideTheSpinner');
            return;
          }

          // Build request body with optional parent references for redirect templates
          const createBody: Record<string, any> = {
            idShop: idShopValue,
            type: templatesType.value,
            baseId: selectedTemplate.id_template,
          };

          // Pass origin_template_id and name if this is a redirect (child template)
          const { baseLinkData } = props;
          if (baseLinkData && baseLinkData.originTemplateId) {
            createBody.originTemplateId = baseLinkData.originTemplateId;
            if (TemplateEditorState.template?.funnel?.template) {
              createBody.rootTemplateId = TemplateEditorState.template.funnel.template.id;
            }
            const generatedName = generateNextStepName(
              baseLinkData.namePrimaryTemplate || '',
              getTemplateParentTypeByType(templatesType.value),
              t,
              baseLinkData.selectedTemplate,
            );
            if (generatedName) {
              createBody.name = generatedName;
            }
          }
          const result = await nestCall(
            'post',
            NestApiType.DEFAULT,
            '/workflow/create-from-base',
            {},
            '',
            createBody,
          );

          if (result && result.success && result.id) {
            showToastSuccess(t('templateSelector.success.created'));
            context.emit('on-choose-template', asInt(result.id), '', templatesType.value);
            store.commit('general/hideTheSpinner');
          } else {
            showToastError(result?.message || t('templateSelector.errors.importFailed'));
            store.commit('general/hideTheSpinner');
          }
        } catch (error) {
          console.error('Template import error:', error);
          showToastError(t('templateSelector.errors.importFailed'));
          store.commit('general/hideTheSpinner');
        }
        return;
      }

      let name: string | null = null;
      // Duplicate template

      const mustDuplicate = props.forceDuplicate || (props.duplicate
      && !(props.originCall === 'templateBuilder' && !props.idCampaign
        && templateCategorySelected.value.category === 'self'
      ));

      if (mustDuplicate) {
        let {
          // eslint-disable-next-line prefer-const
          originCall, idCampaign, baseLinkData,
        } = props;

        if (originCall === 'myTemplates' || (originCall === 'templateBuilder' && !idCampaign)) {
          originCall = null;
          idCampaign = null;
          if (typeof baseLinkData === 'object'
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'namePrimaryTemplate')
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'countRedirectTemplate')
              && baseLinkData.namePrimaryTemplate
              && typeof baseLinkData.countRedirectTemplate === 'number') {
            name = `${baseLinkData.namePrimaryTemplate} - ${t(`templates.channels.${templatesType.value}`)} #${baseLinkData.countRedirectTemplate + 1}`;
          }
        } else {
          // Choose template from campaign
          // eslint-disable-next-line no-lonely-if
          if (typeof baseLinkData === 'object'
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'namePrimaryTemplate')
              && Object.prototype.hasOwnProperty.call(baseLinkData, 'countRedirectTemplate')
              && baseLinkData.namePrimaryTemplate
              && typeof baseLinkData.countRedirectTemplate === 'number') {
            name = `${baseLinkData.namePrimaryTemplate} - ${t(`templates.channels.${templatesType.value}`)} #${baseLinkData.countRedirectTemplate + 1}`;
          } else if (props.currentTemplateName) {
            // Replace mode: preserve the current template name
            name = props.currentTemplateName;
          } else {
            // Add mode: generate incremental name
            const boxTypeToCount = mappingTemplateTypeToOperatorType[templatesType.value];
            const activeFlow = store.getters['flowEditor/getActiveFlow'];
            const countOperatorType = activeFlow?.flowData?.steps
              ? activeFlow.flowData.steps.filter((s: any) => s.type === boxTypeToCount).length
              : getCountOperatorsByType(boxTypeToCount);
            name = `${t(`templates.channels.${templatesType.value}`)} #${countOperatorType + 1}`;
          }
        }

        let originTemplateId = null;
        let rootTemplateId = null;
        if (baseLinkData) {
          originTemplateId = baseLinkData.originTemplateId || null;
        }
        if (originTemplateId && baseLinkData && baseLinkData.namePrimaryTemplate) {
          const { namePrimaryTemplate, selectedTemplate } = baseLinkData;
          name = generateNextStepName(namePrimaryTemplate, getTemplateParentTypeByType(templatesType.value), t, selectedTemplate);
          if (TemplateEditorState.template && TemplateEditorState.template.funnel && TemplateEditorState.template.funnel.template) {
            rootTemplateId = TemplateEditorState.template.funnel.template.id;
          }
        }

        const { duplicateTemplate } = useTemplatesApi();
        duplicateTemplate(idTemplate, {
          name,
          idShopOfTemplate,
          originCall,
          idCampaign,
          originTemplateId,
          rootTemplateId,
        }).then((result) => {
          if (result && result.success && result.data?.id_template) {
            // Small delay to ensure template is available in database before loading
            setTimeout(() => {
              context.emit('on-choose-template', asInt(result.data!.id_template), '', templatesType.value);
            }, 300);
          } else {
            showToastError(t('templateSelector.errors.duplicateFailed'));
            store.commit('general/hideTheSpinner');
          }
        }).catch((error) => {
          console.error('Template duplication error:', error);
          showToastError(t('templateSelector.errors.duplicateFailed'));
          store.commit('general/hideTheSpinner');
        });
      } else {
        context.emit('on-choose-template', idTemplate, label);
      }
    };

    // Handle gallery template selection — import via createFromBase with path as baseId
    const handleGalleryChoose = async (record: GalleryTemplateItem) => {
      // Pre-flight integration gating (same pattern as the campaign pickers).
      if (gatePreflight(record)) return;

      store.commit('general/showTheSpinner');
      try {
        const idShopValue = UserState.activeShop?.id ?? 0;
        if (!idShopValue) {
          showToastError(t('templateSelector.errors.noShop'));
          store.commit('general/hideTheSpinner');
          return;
        }

        if (!record.path) {
          showToastError(t('templateSelector.errors.noPath'));
          store.commit('general/hideTheSpinner');
          return;
        }

        const createBody: Record<string, any> = {
          idShop: idShopValue,
          type: templatesType.value,
          baseId: record.path,
        };

        // Pass origin_template_id and name if this is a redirect (child template)
        const { baseLinkData } = props;
        if (baseLinkData && baseLinkData.originTemplateId) {
          createBody.originTemplateId = baseLinkData.originTemplateId;
          if (TemplateEditorState.template?.funnel?.template) {
            createBody.rootTemplateId = TemplateEditorState.template.funnel.template.id;
          }
          const generatedName = generateNextStepName(
            baseLinkData.namePrimaryTemplate || '',
            getTemplateParentTypeByType(templatesType.value),
            t,
            baseLinkData.selectedTemplate,
          );
          if (generatedName) {
            createBody.name = generatedName;
          }
        }

        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/create-from-base',
          {},
          '',
          createBody,
        );

        // Backend safety net (deactivation between page load and click).
        if (gateBackendResponse(result, record)) {
          store.commit('general/hideTheSpinner');
          return;
        }

        if (result && result.success && result.id) {
          showToastSuccess(t('templateSelector.success.created'));
          context.emit('on-choose-template', asInt(result.id), '', templatesType.value);
        } else {
          showToastError(result?.message || t('templateSelector.errors.importFailed'));
        }
      } catch (error) {
        console.error('Gallery template import error:', error);
        showToastError(t('templateSelector.errors.importFailed'));
      }
      store.commit('general/hideTheSpinner');
    };

    // ========================================================================
    // HTML Preview (uses shared TemplateHtmlPreviewSlider component)
    // ========================================================================
    const htmlPreviewData = ref<{ previews: { html: string; label: string }[] } | null>(null);

    const openHtmlPreview = async (idShopOfTemplate: number, idTemplate: number) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const idTemplateB64 = btoa(String(idTemplate));
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/html-preview-selector',
          {},
          '',
          { idShop: idShopOfTemplate, idTemplate: idTemplateB64 },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || t('templateSelector.htmlPreview.error'));
        }
      } catch {
        showToastError(t('templateSelector.htmlPreview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const closeHtmlPreview = () => {
      htmlPreviewData.value = null;
    };

    const openGalleryHtmlPreview = async (record: GalleryTemplateItem) => {
      htmlPreviewData.value = null;
      store.commit('general/showTheSpinner');

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/workflow/gallery-html-preview',
          {},
          '',
          { path: record.path },
        );

        if (result && result.success && result.previews) {
          htmlPreviewData.value = { previews: result.previews };
        } else {
          showToastError(result?.error || t('templateSelector.htmlPreview.error'));
        }
      } catch {
        showToastError(t('templateSelector.htmlPreview.error'));
      }
      store.commit('general/hideTheSpinner');
    };

    const handleClosePreview = () => {
      displayPreviewModal.value = false;
    };

    const chooseTemplatesType = async (type: string) => {
      templatesType.value = type;
      await getListTemplates(false);
    };

    const handleDialogVisibilityChange = (visible: boolean) => {
      if (!visible) {
        context.emit('on-close-dialog');
      }
    };

    onMounted(async () => {
      // Open dialog after component is mounted to avoid initial @hide trigger
      await nextTick();
      displayDialog.value = true;
      await getListTemplates(false);
    });

    onBeforeUnmount(() => {
      if (scrollObserver) {
        scrollObserver.disconnect();
      }
    });

    const getTemplateTypeFlag = getFeatureFlagForTemplateType;

    // Collapse an integration's many gallery templates into one collection card
    // (anti-flood). Clicking a collection drills into that integration.
    const { displayItems, drilledInIntegration } = useIntegrationCollections(
      () => filteredList.value,
      selectedAuthors,
    );
    const drillIntoIntegration = (slug: string) => {
      selectedAuthors.value = [slug];
    };
    const clearAuthorFilter = () => {
      selectedAuthors.value = [];
    };

    return {
      t,
      displayItems,
      drilledInIntegration,
      drillIntoIntegration,
      clearAuthorFilter,
      idShop,
      baseUrl,
      getTemplateTypeFlag,
      displayPreviewModal,
      isListLoading,
      templateCategorySelected,
      templateCategoriesOptions,
      searchQuery,
      closeModal,
      handleChoose,
      handleClosePreview,
      imageLoadTime,
      imageLoaded,
      imageFailed,
      templatesType,
      displayDialog,
      listTypesConfig,
      chooseTemplatesType,
      handleDialogVisibilityChange,
      getFirstImageKey,
      // Author filter and filtered list
      selectedAuthors,
      authorFilterOptions,
      filteredAuthorOptions,
      authorSearchQuery,
      toggleAuthor,
      getAuthorIcon,
      filteredList,
      getAuthorName,
      getWhatsappStatusLabel,
      getWhatsappStatusIcon,
      getWhatsappStatusColors,
      // Gallery template helpers
      isGalleryItem,
      getLocalizedText,
      handleGalleryChoose,
      // Require-integration modal
      requireIntegrationVisible,
      requireIntegrationData,
      requireIntegrationStatus,
      // HTML preview
      htmlPreviewData,
      openHtmlPreview,
      closeHtmlPreview,
      openGalleryHtmlPreview,
      // Infinite scroll
      totalCount,
      isLoadingMore,
      scrollSentinelRef,
      // Scenario filters
      selectedAutomation,
      automationOptions,
      automationLoading,
      selectedBulk,
      bulkOptions,
      bulkLoading,
      onAutomationShow,
      onBulkShow,
      onCampaignFilter,
    };
  },

  methods: { isDisplayTemplate },
});
